import React from 'react'
import { Button } from '../ui/Button'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import { getActiveOrgId } from '../../utils/orgUtils'
import { fetchSystemGraph, type GraphData, type GraphNode } from '../../services/informationSystemPhase13Api'
import { featureFlags } from '../../config/featureFlags'
import { fetchExecutiveOverview } from '../../services/executiveGovernanceApi'
import graphStyles from '../../styles/components/assets/InformationSystemGraphView.module.scss'

const NODE_COLORS: Record<string, string> = {
  EXECUTIVE_PRESSURE: '#1d4ed8',
  GOVERNANCE_RISK: '#b45309',
  CERTIFICATION_STATUS: '#be123c',
  STANDARD_PRESSURE: '#0369a1',
  SHARED_CONTROL: '#7c2d12',
  INFORMATION_SYSTEM: '#2563eb',
  ASSET: '#059669',
  DATA_CATEGORY: '#7c3aed',
  STANDARD: '#d97706',
  CONTROL: '#0891b2',
  EVIDENCE: '#4b5563',
  REMEDIATION: '#dc2626',
  VULNERABILITY: '#b91c1c',
  RISK: '#9333ea',
}

const LAYER_ORDER = [
  'INFORMATION_SYSTEM',
  'STANDARD',
  'ASSET',
  'VULNERABILITY',
  'DATA_CATEGORY',
  'CONTROL',
  'EVIDENCE',
  'REMEDIATION',
  'RISK',
]

/** Default neighborhood: linked assets, standards, data categories, then GRC overlays. */
const DEFAULT_GRAPH_LAYERS = [
  'assets',
  'standards',
  'data',
  'controls',
  'vulnerabilities',
  'risks',
  'evidence',
  'remediation',
]

const GRAPH_LAYER_OPTIONS = [
  { id: 'assets', labelKey: 'assets.graph.filters.assets' },
  { id: 'standards', labelKey: 'assets.graph.filters.standards' },
  { id: 'data', labelKey: 'assets.graph.filters.dataCategories' },
  { id: 'controls', labelKey: 'assets.graph.filters.controls' },
  { id: 'vulnerabilities', labelKey: 'assets.graph.filters.vulnerabilities' },
  { id: 'risks', labelKey: 'assets.graph.filters.risks' },
  { id: 'evidence', labelKey: 'assets.graph.filters.evidence' },
  { id: 'remediation', labelKey: 'assets.graph.filters.remediation' },
]

function layoutNodes(nodes: GraphNode[]) {
  const byType: Record<string, GraphNode[]> = {}
  for (const t of LAYER_ORDER) byType[t] = []
  for (const n of nodes) {
    const t = byType[n.type] ? n.type : 'CONTROL'
    if (!byType[t]) byType[t] = []
    byType[t].push(n)
  }
  const positioned: Array<GraphNode & { x: number; y: number }> = []
  let y = 40
  for (const type of LAYER_ORDER) {
    const row = byType[type] || []
    if (!row.length) continue
    const cols = Math.min(row.length, 4)
    const rowWidth = cols * 160
    let x = 40
    const startX = Math.max(40, (800 - rowWidth) / 2)
    x = startX
    row.forEach((n, i) => {
      positioned.push({ ...n, x: x + (i % cols) * 168, y })
    })
    y += 88
  }
  return positioned
}

interface Props {
  systemId: string
}

export default function InformationSystemGraphView({ systemId }: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [graph, setGraph] = React.useState<GraphData | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [listMode, setListMode] = React.useState(false)
  const [activeLayers, setActiveLayers] = React.useState<string[]>(DEFAULT_GRAPH_LAYERS)
  const [overlays, setOverlays] = React.useState<Record<string, boolean>>({
    internetExposed: false,
    pciOnly: false,
    evidenceGaps: false,
    remediationBlockers: false,
    criticalPaths: false,
    overlayReadiness: false,
    slaBreach: false,
    staleEvidence: false,
    remediationAging: false,
    blastRadius: false,
    readinessBlockers: false,
    overlayExecutiveGovernance: false,
  })

  const load = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const data = await fetchSystemGraph(token, systemId, orgId, {
        layers: activeLayers.join(','),
        failedControlsOnly: overlays.criticalPaths,
        internetExposed: overlays.internetExposed,
        pciOnly: overlays.pciOnly,
        evidenceGaps: overlays.evidenceGaps,
        remediationBlockers: overlays.remediationBlockers,
        criticalPaths: overlays.criticalPaths,
        overlayReadiness: overlays.overlayReadiness,
        slaBreach: overlays.slaBreach,
        staleEvidence: overlays.staleEvidence,
        remediationAging: overlays.remediationAging,
        blastRadius: overlays.blastRadius,
        readinessBlockers: overlays.readinessBlockers,
      })
      let merged = data
      if (featureFlags.EXECUTIVE_GOVERNANCE_V1 && overlays.overlayExecutiveGovernance) {
        try {
          const exec = await fetchExecutiveOverview()
          const overlay = exec.graphOverlay as { nodes?: GraphNode[]; edges?: GraphData['edges'] } | undefined
          if (overlay?.nodes?.length) {
            const execNodes = overlay.nodes.map((n, i) => ({
              ...n,
              id: n.id || `exec-${i}`,
              type: n.type || 'EXECUTIVE_PRESSURE',
              x: 600 + (i % 3) * 120,
              y: 20 + Math.floor(i / 3) * 60,
            }))
            merged = {
              ...data,
              nodes: [...data.nodes, ...execNodes],
              edges: [...(data.edges || []), ...(overlay.edges || [])],
              summary: {
                ...data.summary,
                nodeCount: data.summary.nodeCount + execNodes.length,
                edgeCount: data.summary.edgeCount + (overlay.edges?.length || 0),
              },
            }
          }
        } catch {
          /* executive overlay optional */
        }
      }
      setGraph(merged)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('assets.graph.error.load', 'Failed to load graph'))
    } finally {
      setLoading(false)
    }
  }, [token, systemId, orgId, t, activeLayers, overlays])

  React.useEffect(() => {
    load()
  }, [load])

  if (loading) return <p className="text-sm text-gray-500">{t('common.loading')}</p>
  if (error) {
    return (
      <p className="text-sm text-red-600">
        {error}{' '}
        <Button type="button" size="small" color="outline" onClick={() => load()}>
          {t('common.retry', 'Retry')}
        </Button>
      </p>
    )
  }
  if (!graph || graph.nodes.length === 0) {
    return <p className="text-sm text-gray-500">{t('assets.graph.empty', 'No relationship data to display yet.')}</p>
  }

  const positioned = layoutNodes(graph.nodes)
  const posById = Object.fromEntries(positioned.map((n) => [n.id, n]))
  const missing = graph.summary?.missingAreas || []

  const overlayOptions = [
    { id: 'internetExposed', labelKey: 'assets.graph.overlays.internetExposed' },
    { id: 'pciOnly', labelKey: 'assets.graph.overlays.pciOnly' },
    { id: 'evidenceGaps', labelKey: 'assets.graph.overlays.evidenceGaps' },
    { id: 'remediationBlockers', labelKey: 'assets.graph.overlays.remediationBlockers' },
    { id: 'criticalPaths', labelKey: 'assets.graph.overlays.criticalPaths' },
    { id: 'overlayReadiness', labelKey: 'assets.graph.overlays.readiness' },
    { id: 'slaBreach', labelKey: 'assets.graph.overlays.slaBreach' },
    { id: 'staleEvidence', labelKey: 'assets.graph.overlays.staleEvidence' },
    { id: 'remediationAging', labelKey: 'assets.graph.overlays.remediationAging' },
    { id: 'blastRadius', labelKey: 'assets.graph.overlays.blastRadius' },
    { id: 'readinessBlockers', labelKey: 'assets.graph.overlays.readinessBlockers' },
    ...(featureFlags.EXECUTIVE_GOVERNANCE_V1
      ? [{ id: 'overlayExecutiveGovernance', labelKey: 'assets.graph.overlays.executiveGovernance' }]
      : []),
  ]

  return (
    <div className={graphStyles.root}>
      <div className={graphStyles.toolbarRow}>
        <p className={graphStyles.metaText}>
          {graph.summary.nodeCount} {t('assets.graph.nodesLabel', 'nodes')}, {graph.summary.edgeCount}{' '}
          {t('assets.graph.edges', 'edges')}
        </p>
        <Button type="button" size="small" color="outline" onClick={() => setListMode((v) => !v)}>
          {listMode ? t('assets.graph.viewGraph', 'Graph view') : t('assets.graph.viewList', 'List view')}
        </Button>
      </div>

      <div className={graphStyles.filterPanel}>
        <p className={graphStyles.filterGroupLabel}>{t('assets.graph.overlaysTitle', 'Overlays')}</p>
        <div className={graphStyles.filterChips}>
          {overlayOptions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`${graphStyles.filterChip} ${overlays[opt.id] ? graphStyles.filterChipOverlayActive : ''}`.trim()}
              onClick={() => setOverlays((prev) => ({ ...prev, [opt.id]: !prev[opt.id] }))}
            >
              {t(opt.labelKey, opt.id)}
            </button>
          ))}
        </div>
        <p className={graphStyles.filterGroupLabel}>{t('assets.graph.layersTitle', 'Layers')}</p>
        <div className={graphStyles.filterChips}>
          {GRAPH_LAYER_OPTIONS.map((opt) => {
            const on = activeLayers.includes(opt.id)
            return (
              <button
                key={opt.id}
                type="button"
                className={`${graphStyles.filterChip} ${on ? graphStyles.filterChipActive : ''}`.trim()}
                onClick={() =>
                  setActiveLayers((prev) =>
                    prev.includes(opt.id) ? prev.filter((x) => x !== opt.id) : [...prev, opt.id],
                  )
                }
              >
                {t(opt.labelKey, opt.id)}
              </button>
            )
          })}
        </div>
      </div>

      {missing.length > 0 ? (
        <ul className={graphStyles.missingList}>
          {missing.map((m) => (
            <li key={m}>{t(`assets.graph.missing.${m}`, m)}</li>
          ))}
        </ul>
      ) : null}

      {listMode ? (
        <div className={`${graphStyles.listPanel} text-sm space-y-3`}>
          {LAYER_ORDER.map((type) => {
            const group = graph.nodes.filter((n) => n.type === type)
            if (!group.length) return null
            return (
              <div key={type}>
                <h5 className="font-medium text-gray-700">{t(`assets.graph.nodeType.${type}`, type)}</h5>
                <ul className="list-disc pl-5 text-gray-600">
                  {group.map((n) => (
                    <li key={n.id}>{n.label}</li>
                  ))}
                </ul>
              </div>
            )
          })}
          {graph.edges.length > 0 ? (
            <div>
              <h5 className="font-medium text-gray-700">{t('assets.graph.edges', 'Relationships')}</h5>
              <ul className="text-xs text-gray-600 space-y-1">
                {graph.edges.map((e) => (
                  <li key={e.id}>
                    {t(`assets.graph.edgeType.${e.type}`, e.type)}: {posById[e.source]?.label || e.source} →{' '}
                    {posById[e.target]?.label || e.target}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={`${graphStyles.graphCanvas} overflow-x-auto bg-slate-50 p-2`}>
          <svg width={Math.max(820, positioned.reduce((m, n) => Math.max(m, n.x + 150), 0))} height={Math.max(320, positioned.reduce((m, n) => Math.max(m, n.y + 60), 0))}>
            {graph.edges.map((e) => {
              const s = posById[e.source]
              const tgt = posById[e.target]
              if (!s || !tgt) return null
              return (
                <line
                  key={e.id}
                  x1={s.x + 70}
                  y1={s.y + 24}
                  x2={tgt.x + 70}
                  y2={tgt.y + 8}
                  stroke="#94a3b8"
                  strokeWidth={1}
                />
              )
            })}
            {positioned.map((n) => (
              <g key={n.id}>
                <rect x={n.x} y={n.y} width={140} height={48} rx={6} fill={NODE_COLORS[n.type] || '#64748b'} opacity={0.9} />
                <text x={n.x + 8} y={n.y + 16} fill="#fff" fontSize={9} fontWeight={600}>
                  {t(`assets.graph.nodeType.${n.type}`, n.type).slice(0, 14)}
                </text>
                <text x={n.x + 8} y={n.y + 32} fill="#fff" fontSize={10}>
                  {n.label.length > 18 ? `${n.label.slice(0, 16)}…` : n.label}
                </text>
              </g>
            ))}
          </svg>
        </div>
      )}
    </div>
  )
}
